import { Component, OnInit, ViewChild, ElementRef, OnDestroy, Input, SimpleChanges, OnChanges } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Emoji, EmojiData } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { fromEvent, ReplaySubject, startWith, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { formatUserName } from 'app/core/utilities/user-util';
import { AppState } from 'app/types';
import { CustomValidators } from 'app/utils/custom.validators';
import User from 'entity/User';
import { ChatStatus, OutgoingMessageType } from 'messages/websocket.messages';

import { ChatService } from '../../services/chat.service';
import { DefaultEmojis } from '../../services/emote/emote.service';
import { SceneService } from '../../services/scene.service';
import { WebsocketService } from '../../services/websocket.service';
import { Keys } from '../../social-pixi-canvas/keys';
import { socialAppSelectors } from '../../state/app';
import SocialChatMessage from '../social-chat-message/social-chat-message';

@Component({
  selector: 'app-social-chat-composer',
  templateUrl: './social-chat-composer.component.html',
  styleUrls: ['./social-chat-composer.component.scss'],
  standalone: false,
})
export class SocialChatComposerComponent implements OnInit, OnDestroy, OnChanges {
  public form?: UntypedFormGroup;
  public messageType = 'public';
  private userId?: string;
  public users: User[] = [];
  private originalList: User[] = [];
  private formDefaultMessage = 'Enter your message here';
  @ViewChild('messageTextArea') private messageTextArea?: ElementRef;
  @ViewChild('chatEmojiPicker', { static: false }) private emojiPickerElement!: ElementRef;
  @ViewChild('chatEmojiPickerButton', { static: false }) private emojiPickerButtonElement!: ElementRef;

  @Input() public moderatedChat = false;
  @Input() public tabSelected?: string;
  @Input() public replyUserId?: string;

  private destroyed$ = new ReplaySubject<boolean>(1);
  protected emotes = false;
  protected emotesPickerOpen = false;
  private hostIdList?: string[];
  protected defaultEmojis: string[] = [];
  protected isHostFeed = false;

  public constructor(
    private sceneService: SceneService,
    private formBuilder: UntypedFormBuilder,
    protected chatService: ChatService,
    private websocketService: WebsocketService,
    private store: Store<AppState>,
  ) {
    this.defaultEmojis = DefaultEmojis;
    this.store
      .select(coreSelectors.getUserId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userId) => (this.userId = userId));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.emotes)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((emotes) => (this.emotes = emotes));

    this.store
      .select(socialAppSelectors.getHostIdList)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hostIdList) => (this.hostIdList = hostIdList));
  }

  public ngOnInit(): void {
    this.buildForm();

    fromEvent(document, 'click')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event) => {
        this.windowClick(event);
      });

    // Subscribe to the users list to build the select
    this.sceneService.sceneManager?.participantsCountChange
      .pipe(startWith(0), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.users = this.sceneService
          .getAllUsers()
          .filter(({ userId }) => userId !== this.userId)
          .sort((a, b) => formatUserName(a).localeCompare(formatUserName(b)));

        if (this.moderatedChat && !!this.hostIdList?.length) {
          this.users.sort((a, b) => {
            // First, sort by the isHost flag
            if (this.isUserHost(a) && !this.isUserHost(b)) {
              return -1; // a is a host and b is not, so a comes first
            } else if (!this.isUserHost(a) && this.isUserHost(b)) {
              return 1; // b is a host and a is not, so b comes first
            } else {
              return 0;
            }
          });

          if (this.replyUserId) {
            this.filterUsersList(this.replyUserId);
          }
        }
      });

    // Subscribe to receiver change from other areas of social
    this.chatService.chatReceiver.pipe(takeUntil(this.destroyed$)).subscribe((receiver) => {
      if (receiver) {
        this.form?.get('receiver')?.patchValue(receiver);
        this.messageTextArea?.nativeElement.focus();
        this.setMessageType(receiver);
      }
    });

    // Detect change in message receiver to define label public|private
    this.form?.controls.receiver.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
      this.setMessageType(value);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.tabSelected && changes.tabSelected.currentValue) {
      this.isHostFeed = changes.tabSelected.currentValue === 'Hosts';
    }
  }

  private windowClick(event: Event) {
    if (this.emotesPickerOpen) {
      const clickedInside = this.emojiPickerElement.nativeElement.contains(event.target);
      const clickedInsideButton = this.emojiPickerButtonElement.nativeElement.contains(event.target);

      if (!clickedInside && !clickedInsideButton) {
        event.preventDefault();
        event.stopPropagation();
        this.closeEmojiPicker();
      }
    }
  }

  protected addEmoji($event: Emoji) {
    if (this.messageTextArea) {
      // Get the emoji
      const emojiData = $event.emoji as EmojiData;
      if (emojiData.native) {
        this.messageTextArea.nativeElement.focus();
        // Get the current caret position and add the emoji after it
        const selectionEnd = this.messageTextArea.nativeElement.selectionEnd;
        const currentValue = this.form?.get('message')?.value;
        const newValue =
          currentValue.substring(0, selectionEnd) + emojiData.native + currentValue.substring(selectionEnd);
        this.form?.get('message')?.setValue(newValue);
        // the following 2 lines set the caret position behind the emoji
        this.messageTextArea.nativeElement.selectionStart = selectionEnd + emojiData.native.length;
        this.messageTextArea.nativeElement.selectionEnd = selectionEnd + emojiData.native.length;
        this.closeEmojiPicker();
      }
    }
  }

  protected toggleEmotePicker(): void {
    this.emotesPickerOpen = !this.emotesPickerOpen;
  }

  protected closeEmojiPicker(): void {
    this.emotesPickerOpen = false;
  }

  private setMessageType(value: string) {
    this.messageType = value === 'all' ? 'public' : 'private';
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private buildForm(): void {
    // Build Form
    this.form = this.formBuilder.group({
      receiver: ['all', Validators.required],
      message: [
        this.formDefaultMessage,
        [
          Validators.required,
          Validators.maxLength(4096),
          CustomValidators.notFilledWithWhitespace,
          CustomValidators.stringNotEqualTo(this.formDefaultMessage),
        ],
      ],
    });
  }

  public onMessageFocus(): void {
    if (this.formDefaultMessage === this.form?.get('message')?.value) {
      this.form?.get('message')?.setValue('');
    }
  }

  public onMessageBlur(): void {
    if ('' === this.form?.get('message')?.value) {
      this.form?.get('message')?.setValue(this.formDefaultMessage);
    }
  }

  public onMessageKeyDown(event: KeyboardEvent): void {
    event.stopPropagation();
    if (event.code === Keys.KEY_ENTER && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage(false);
    }
  }

  public sendMessage(resetDefaultMessage = false): void {
    if (!this.form || this.form?.invalid) {
      return;
    }

    const privacy = this.setPrivacy(this.form?.controls.receiver.value);
    const userIds = this.setUserIds(this.form?.controls.receiver.value);
    const objectMessage = {
      t: OutgoingMessageType.CHATSEND,
      group: '',
      text: this.form?.controls.message.value,
      userIds,
      privacy,
      sentDate: Date.now(),
    };

    // Add to My Chat View
    this.chatService.addMessageISent(this.form?.controls.message.value, privacy, userIds, objectMessage.sentDate);

    // Send chat message through web socket message
    this.websocketService.sendMessage(objectMessage);

    // Reset textarea value
    const newTextAreaValue = resetDefaultMessage ? this.formDefaultMessage : '';
    this.form?.get('message')?.setValue(newTextAreaValue);

    //Close emoji picker if open
    if (this.emotesPickerOpen) {
      this.emotesPickerOpen = false;
    }

    if (this.chatService.userIdToReply) {
      this.approveMessageOnReply(this.chatService.repliedMsg);
      this.chatService.setUserIdToReply(undefined);
      this.users = cloneDeep(this.originalList);
    }
  }

  protected isUserHost(user: User): boolean {
    return this.hostIdList?.find((hostId) => hostId === user.userId) ? true : false;
  }

  private setPrivacy(value: string): string {
    if (this.isHostFeed) {
      return 'hosts';
    } else {
      return value === 'all' ? 'public' : 'private';
    }
  }

  private setUserIds(value: string): string | string[] {
    if (this.isHostFeed) {
      return 'hosts';
    } else {
      return value === 'all' ? 'all' : [this.form?.controls.receiver.value];
    }
  }

  private filterUsersList(userId: string): void {
    this.originalList = cloneDeep(this.users);
    const filteredList = this.users.filter((user) => user.userId === userId);
    this.users = filteredList;
  }

  public cancelReply(): void {
    this.chatService.setUserIdToReply(undefined);
    this.users = cloneDeep(this.originalList);
  }

  private approveMessageOnReply(msg?: SocialChatMessage): void {
    if (!msg) {
      return;
    }

    const objectMessage = {
      t: OutgoingMessageType.CHATMODERATE,
      fromUserId: msg.sender.userId,
      text: msg.message,
      sentDate: msg.sentTimestamp,
      status: ChatStatus.REPLIED,
    };

    // Send chat message through web socket message
    this.websocketService.sendMessage(objectMessage);
  }
}
